import { useCallback, useEffect, useRef, useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import api from '../../../api';

/**
 * The hotel's live answer for one exact rate (dream-be
 * `POST /response-artifacts/:id/hotel-quote`): the price the user will be
 * charged and the terms they are agreeing to, as the supplier has just
 * confirmed them, rather than what the search snapshot showed.
 */
export interface HotelRateQuote {
  quoteId: string;
  total: number;
  currency: string;
  board: string;
  refundable: boolean;
  freeCancellationUntil: string | null;
  cancellationTerms: string;
  expiresAt: string;
  changed: { price: boolean; terms: boolean };
  shown: { total: number; currency: string; cancellationTerms: string };
}

export type HotelRateQuoteFailure =
  | 'invalid_offer'
  | 'offer_expired'
  | 'supplier_failed'
  | 'request_failed';

export type HotelRateQuoteState =
  | { status: 'idle' }
  | { status: 'quoting' }
  | { status: 'quoted'; quote: HotelRateQuote }
  | { status: 'expired'; quote: HotelRateQuote }
  | { status: 'failed'; code: HotelRateQuoteFailure; message: string };

interface QuoteRequest {
  artifactId: string;
  choiceId: string;
  offerId: string;
  shareToken?: string;
}

interface QuoteResponse {
  success: boolean;
  quote?: HotelRateQuote;
  code?: string;
  error?: string;
}

function isSupplierFailureCode(
  code: unknown,
): code is 'invalid_offer' | 'offer_expired' | 'supplier_failed' {
  return (
    code === 'invalid_offer' ||
    code === 'offer_expired' ||
    code === 'supplier_failed'
  );
}

function responseBody(error: unknown): QuoteResponse | undefined {
  if (typeof error !== 'object' || error === null) return undefined;
  const { response } = error as { response?: { data?: unknown } };
  const data = response?.data;
  if (typeof data !== 'object' || data === null) return undefined;
  const { code, error: message } = data as Record<string, unknown>;
  return {
    success: false,
    ...(typeof code === 'string' ? { code } : {}),
    ...(typeof message === 'string' ? { error: message } : {}),
  };
}

function failureFrom(error: unknown): {
  code: HotelRateQuoteFailure;
  message: string;
} {
  const body = responseBody(error);
  if (body) {
    const { code, error: message } = body;
    if (isSupplierFailureCode(code)) {
      return {
        code,
        message:
          message || "The hotel couldn't confirm this rate. Try again shortly.",
      };
    }
    if (message) return { code: 'request_failed', message };
  }
  return {
    code: 'request_failed',
    message:
      "We couldn't reach the hotel to confirm the price. Check your connection and try again.",
  };
}

/**
 * Asks the hotel for its live price and terms the moment the confirm sheet
 * opens on an exact rate, so what the user approves is the supplier's
 * current figure. Re-quotes when the request changes and flips to
 * `expired` once the quote's window closes; `refresh` asks again.
 */
export function useHotelRateQuote(request: QuoteRequest | null): {
  state: HotelRateQuoteState;
  refresh: () => void;
} {
  const { getAccessTokenSilently, isAuthenticated } = useAuth0();
  // Each answer is tagged with the rate it was asked for, so switching rates
  // never shows (or lets the user approve) the previous rate's quote during
  // the render before the new request starts.
  const [held, setHeld] = useState<{
    key: string | null;
    state: HotelRateQuoteState;
  }>({ key: null, state: { status: 'idle' } });
  const [round, setRound] = useState(0);
  const runRef = useRef(0);

  const artifactId = request?.artifactId ?? null;
  const choiceId = request?.choiceId ?? null;
  const offerId = request?.offerId ?? null;
  const shareToken = request?.shareToken;
  const key =
    artifactId && choiceId && offerId
      ? `${artifactId}\u0000${choiceId}\u0000${offerId}`
      : null;
  const setState = useCallback(
    (state: HotelRateQuoteState) => setHeld({ key, state }),
    [key],
  );

  useEffect(() => {
    const run = ++runRef.current;
    if (!artifactId || !choiceId || !offerId) {
      setState({ status: 'idle' });
      return;
    }
    setState({ status: 'quoting' });
    let cancelled = false;
    (async () => {
      try {
        const headers =
          !shareToken && isAuthenticated
            ? { Authorization: `Bearer ${await getAccessTokenSilently()}` }
            : undefined;
        const response = await api.post<QuoteResponse>(
          `/response-artifacts/${encodeURIComponent(artifactId)}/hotel-quote`,
          { choiceId, offerId, ...(shareToken ? { shareToken } : {}) },
          headers ? { headers } : undefined,
        );
        if (cancelled || run !== runRef.current) return;
        const quote = response.data.quote;
        if (!response.data.success || !quote) {
          setState({
            status: 'failed',
            code: 'request_failed',
            message:
              response.data.error ||
              "The hotel didn't confirm this rate. Try again shortly.",
          });
          return;
        }
        setState({ status: 'quoted', quote });
      } catch (error) {
        if (cancelled || run !== runRef.current) return;
        setState({ status: 'failed', ...failureFrom(error) });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [
    artifactId,
    choiceId,
    offerId,
    shareToken,
    round,
    getAccessTokenSilently,
    isAuthenticated,
    setState,
  ]);

  const state: HotelRateQuoteState =
    held.key === key
      ? held.state
      : key
        ? { status: 'quoting' }
        : { status: 'idle' };

  useEffect(() => {
    if (state.status !== 'quoted') return;
    const remaining = new Date(state.quote.expiresAt).getTime() - Date.now();
    if (!Number.isFinite(remaining)) return;
    const timer = window.setTimeout(
      () => setState({ status: 'expired', quote: state.quote }),
      Math.max(0, remaining),
    );
    return () => window.clearTimeout(timer);
  }, [state, setState]);

  const refresh = useCallback(() => setRound((n) => n + 1), []);

  return { state, refresh };
}
